<template>
  <div
    class="questions-nav"
    :class="showSeparator && 'questions-nav--show-separator'"
  >
    <div
      v-if="isLoading"
      :class="[
        'questions-nav__btn-wrapper',
        'questions-nav__saving-wrapper',
        examLayoutV2 && 'questions-nav__btn-wrapper--layout-v2',
      ]"
    >
      <div class="questions-nav__saving">
        <span class="dot-flashing"></span>Saving
      </div>
    </div>
    <div
      v-else
      :class="[
        'questions-nav__btn-wrapper',
        examLayoutV2 && 'questions-nav__btn-wrapper--layout-v2',
      ]"
    >
      <div
        class="questions-nav__action-button"
        :style="
          currentQuestionId === firstQuestionId
            ? 'pointer-events: none; opacity: 0.5'
            : ''
        "
      >
        <button
          class="btn btn--secondary btn--secondary--with-icon btn--secondary--with-icon--left questions-nav__btn questions-nav__btn__prev btn--default-icon-color"
          :disabled="isLoading"
          @click="$emit('prev-question')"
        >
          <ArrowLeftIcon class="questions-nav__btn__prev--icon" />
          {{ previousButtonLabel }}
        </button>
      </div>
      <div
        v-if="showActionButtons"
        class="questions-nav__action-buttons questions-nav__action-buttons__tools"
      >
        <span
          v-if="showQuestionFeedbackBtn"
          v-tooltip="{
            content: 'Provide feedback on this question',
            placement: 'top-center',
          }"
          class="questions-nav__action-buttons__wrapper"
          :class="{
            'questions-nav__action-buttons__wrapper--active': hasFeedback,
          }"
          @click="$emit('open-question-feedback')"
          ><FeedbackIcon
        /></span>
        <span
          v-tooltip="{
            content: tooltipText,
            placement: 'top-center',
          }"
          class="questions-nav__action-buttons__wrapper questions-nav__action-buttons__wrapper-pin"
          :class="{
            'questions-nav__action-buttons__wrapper--active': hasFlagged,
          }"
          @click="$emit('flag-question')"
          ><PinIcon
        /></span>
      </div>
      <div
        class="questions-nav__action-buttons questions-nav__action-buttons__with-time"
      >
        <button
          v-if="!isLastQuestion || showFinished"
          class="btn btn--primary btn--primary--with-icon btn--primary--with-icon--right questions-nav__btn questions-nav__btn__next btn--default-icon-color"
          :disabled="isLoading"
          @click="$emit('next-question')"
        >
          {{ nextButtonLabel }}
          <ArrowRightIcon class="questions-nav__btn__next--icon" />
        </button>
      </div>
      <div
        v-if="formattedLastSave"
        :class="[
          'questions-nav__saved-time',
          isLastQuestion && 'questions-nav__saved-time--last-question',
        ]"
      >
        <span class="questions-nav__saved-time--highlighted">
          <CheckboxTick /> Saved.
        </span>
        Progress saved {{ formattedLastSave }}.
      </div>
    </div>
  </div>
</template>

<script>
import { ArrowLeftIcon, ArrowRightIcon } from 'vue-feather-icons'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import FeedbackIcon from '@/assets/images/certification/message-circle.svg'
import PinIcon from '@/assets/images/certification/pin-icon.svg'
import { formatDistanceToNow } from 'date-fns'
import CONSTANTS from '@/constants'

export default {
  name: 'QuestionsNav',
  components: {
    ArrowLeftIcon,
    ArrowRightIcon,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
    FeedbackIcon: { ...FeedbackIcon, name: 'FeedbackIcon' },
    PinIcon: { ...PinIcon, name: 'PinIcon' },
  },
  props: {
    currentQuestion: {
      type: Object,
      required: true,
    },
    lastQuestion: {
      type: Object,
      required: true,
    },
    firstQuestion: {
      type: Object,
      required: true,
    },
    showActionButtons: {
      type: Boolean,
      required: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
    },
    showFinished: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSeparator: {
      type: Boolean,
      required: false,
      default: false,
    },
    examLayoutV2: {
      type: Boolean,
      required: false,
      default: false,
    },
    lastSavedDate: {
      type: Date,
      required: false,
    },
    attempt: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      savingTimeIntervalId: null,
    }
  },
  computed: {
    currentQuestionId() {
      return this.currentQuestion.question_id || this.currentQuestion.uuid
    },
    lastQuestionId() {
      return this.lastQuestion.question_id || this.lastQuestion.uuid
    },
    isLastQuestion() {
      return this.currentQuestionId === this.lastQuestionId
    },
    firstQuestionId() {
      return this.firstQuestion.question_id || this.firstQuestion.uuid
    },
    tooltipText() {
      return this.hasFlagged
        ? 'Unpin'
        : 'Pin this question as a reminder to come back later'
    },
    nextButtonLabel() {
      if (this.isLastQuestion) return 'Finish'
      return this.examLayoutV2 ? 'Next' : 'Next question'
    },
    previousButtonLabel() {
      return this.examLayoutV2 ? 'Previous' : 'Previous question'
    },
    isEWPTX() {
      return (
        this.attempt.certification.id === CONSTANTS.CERTIFICATIONS.IDS.eWPTX
      )
    },
    showQuestionFeedbackBtn() {
      return this.isBeta || this.isEWPTX
    },
    hasFeedback() {
      return !!this.currentQuestion.feedback
    },
    hasFlagged() {
      return !!this.currentQuestion.is_flagged
    },
    formattedLastSave() {
      const lastSavedDate = this.lastSavedDate

      if (!lastSavedDate) return ''

      return formatDistanceToNow(lastSavedDate, {
        addSuffix: true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.questions-nav {
  &--show-separator {
    border-top: 1px solid #424242;
  }
  &__action-buttons {
    display: flex;
    justify-content: space-around;
    min-width: 145px;
    align-items: center;
    &__wrapper {
      background: var(--glass-mid-gray);
      width: 60px;
      border-radius: 30px;
      height: 60px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: 0.2s all ease;
      svg {
        path {
          transition: 0.2s all ease;
        }
      }
      &:hover:not(&-pin) {
        background: var(--menu-subcategory);
        svg {
          path {
            stroke: var(--white);
            fill: none;
          }
        }
      }
      &-pin:not(&--active) {
        &:hover {
          background: var(--white);
        }
        svg {
          rect {
            fill-opacity: 0;
          }
        }
      }
      &--active {
        background: transparent
          linear-gradient(180deg, #ef7d22 0%, #ee3b00 100%) 0% 0% no-repeat
          padding-box;
        svg {
          path {
            stroke: var(--white);
            fill: var(--white);
          }
        }
      }
    }

    &__with-time {
      display: flex;
      flex-direction: column-reverse;
      align-items: flex-end;
      justify-content: flex-end;

      @include tablet {
        flex-direction: column-reverse;
      }
      gap: 26px;
    }
  }
  &__icon {
    position: absolute;
    top: -1px;
    right: -1px;
    &--filled {
      fill: var(--green);
    }
  }
  &__btn {
    cursor: pointer;
    &-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;
      min-height: 110px;

      &--layout-v2 {
        border-top: none;
        position: relative;
        padding: unset;
        min-height: auto;
        align-items: flex-start;
        margin-top: 50px;
      }
    }
    &__next {
      order: 1;
    }
  }
  &__saving {
    display: flex;
    align-items: center;
    width: 100px;
    justify-content: space-around;
    margin-right: 10px;
    padding: 19px 0 20px 0;
    &-wrapper {
      justify-content: flex-end;
    }
  }
  &__saved-time {
    width: 100%;
    text-align: right;
    font-size: 0.75rem;
    color: var(--silver-chalice);
    margin-top: 20px;
    padding-right: 10px;
    order: 0;
    position: relative;
    @include tablet {
      margin: 26px 0 30px 0;
      font-size: 0.75rem;
      padding-right: 0;
    }
    @include lg-desktop {
      font-size: 0.85rem;
    }
    &--highlighted {
      margin-right: 5px;
      color: var(--white);
      animation-delay: 2s;
      animation-duration: 2s;
      animation-name: fadeOut;
      animation-fill-mode: forwards;
      svg {
        margin-right: 5px;
        path {
          stroke: var(--green);
        }
      }
      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    }
    &--last-question {
      @include tablet {
        width: 100%;
        .answers__saved-time--highlighted {
          right: 10px;
        }
      }
    }
  }
  img {
    max-width: 450px;
  }
}
</style>
